// src/GaloEUProject.js
// ---------------------------------------------------------------------------
// EU PROJEKT / EU PROJECT — obavezna objava podataka o projektu (vidljivost EU)
//
// SVI TEKSTOVI SU U OBJEKTU `project` NIŽE.
// Za izmjenu sadržaja mijenjaj SAMO taj objekt — ne diraj JSX ispod njega.
// Svako polje ima { hr, en } — hrvatski je obavezan, engleski je prijevod.
//
// ALL COPY LIVES IN THE `project` OBJECT BELOW.
// To change wording, edit only that object — leave the JSX underneath alone.
// ---------------------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Building2,
  CalendarDays,
  Euro,
  Hash,
  Users,
  Target,
  Layers,
  Mail,
  CheckCircle2,
  ArrowUp,
  Crosshair,
  Timer,
  Gauge,
  TrendingUp,
  Stethoscope,
  GraduationCap,
  FileText,
} from 'lucide-react';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Container from './components/common/Container';

// ===========================================================================
// PODACI O PROJEKTU / PROJECT DATA  — ovdje se uređuje sav sadržaj
// ===========================================================================

const EU_LOGO = '/img/eu.png';
const EU_LOGO_ALT = 'Financira Europska unija — amblem Europske unije';

const project = {
  code: 'PK.1.1.12.0221',
  acronym: 'IOM',

  title: {
    hr: 'Razvoj prvog autonomnog 5-osnog preciznog robotskog sustava za intraoralnu pripremu zuba brušenjem za fiksno protetske nadomjestke — IOM (Intraoral Milling)',
    en: 'Development of the first autonomous 5-axis precision robotic system for intraoral tooth preparation by grinding for fixed prosthetic restorations — IOM (Intraoral Milling)',
  },

  // --- Poziv / programme ---------------------------------------------------
  programme: {
    call: {
      hr: 'IRI S3 — Povećanje razvoja novih proizvoda i usluga koji proizlaze iz aktivnosti istraživanja i razvoja',
      en: 'IRI S3 — Increasing the development of new products and services arising from research and development activities',
    },
    callCode: 'PK.1.1.12',
    operationalProgramme: {
      hr: 'Program Konkurentnost i kohezija 2021. — 2027.',
      en: 'Competitiveness and Cohesion Programme 2021—2027',
    },
    fund: {
      hr: 'Europski fond za regionalni razvoj (EFRR)',
      en: 'European Regional Development Fund (ERDF)',
    },
  },

  beneficiary: 'GALO INDUSTRIES d.o.o. za usluge',

  partners: [
    'Sveučilište u Zagrebu, Stomatološki fakultet',
    'ICPE Internacionalni centar za profesionalnu edukaciju d.o.o. za usluge i trgovinu',
    'EMC Precision d.o.o. za inženjering',
  ],

  period: '02.01.2026. — 02.01.2029.',

  finance: {
    total: '2.080.363,44 €',
    eligible: '2.004.505,77 €',
    euSupport: '1.385.014,00 €',
  },

  contactEmail: 'info@galo-industries.com',

  // --- Dva rezultata projekta / the two project results --------------------
  results: {
    heading: { hr: 'Dva povezana rezultata', en: 'Two connected results' },
    lead: {
      hr: 'Projekt IOM usmjeren je na razvoj prvog cjelovitog sustava koji spaja intraoralnu robotiku i digitalnu edukaciju u dentalnoj medicini. U njegovom središtu nalaze se dva povezana rezultata:',
      en: 'The IOM project is focused on developing the first complete system that combines intraoral robotics with digital education in dental medicine. At its core are two connected results:',
    },
    items: [
      {
        letter: 'a',
        name: 'IOM-CLINIC',
        icon: Stethoscope,
        hr: 'Mali robot s pet stupnjeva slobode koji radi unutar usne šupljine i služi za precizno oblikovanje zuba.',
        en: 'A compact robot with five degrees of freedom that operates inside the oral cavity and is used for precise tooth shaping.',
      },
      {
        letter: 'b',
        name: 'IOM-SIM',
        icon: GraduationCap,
        hr: 'Edukacijska platforma koja kombinira virtualnu stvarnost i haptičku (osjetnu) povratnu informaciju.',
        en: 'An educational platform combining virtual reality with haptic (tactile) feedback.',
      },
    ],
  },

  // --- Ciljane performanse / target performance ----------------------------
  metrics: {
    heading: { hr: 'Ciljane performanse', en: 'Target performance' },
    items: [
      {
        icon: Crosshair,
        value: '±20 µm',
        hr: 'Točnost rada',
        en: 'Working accuracy',
      },
      {
        icon: Timer,
        value: '2—4 min',
        hr: 'Trajanje postupka po zubu',
        en: 'Procedure time per tooth',
      },
      {
        icon: Gauge,
        value: '15 → 5 min',
        hr: 'Skraćenje zahvata po zubu',
        en: 'Procedure time reduction per tooth',
      },
      {
        icon: TrendingUp,
        value: 'TRL 2 → 8',
        hr: 'Razina tehnološke spremnosti',
        en: 'Technology readiness level',
      },
    ],
  },

  // --- Kratki opis projekta / Project summary ------------------------------
  summary: {
    heading: { hr: 'Kratki opis projekta', en: 'Project summary' },
    body: {
      hr: [
        'IOM-SIM koristi digitalni blizanac kliničkog robota i omogućava standardizirano učenje i procjenu vještina studenata i doktora dentalne medicine. Sustav se temelji na personaliziranom 3D predlošku, posebnom nosaču u obliku „helmet” konstrukcije koji smanjuje opterećenje, sterilizabilnim motorima i senzorima, te upravljačkom sloju u stvarnom vremenu uz podršku umjetne inteligencije koja kompenzira sitne pokrete pacijenta.',
        'Ciljane performanse uključuju točnost rada unutar ±20 µm i trajanje postupka od 2–4 minute po zubu, što je višestruko bolje od ručnog rada i postojećih robotskih rješenja koja djeluju izvan usne šupljine. Projekt počinje na TRL-u 2 i planira doseći TRL-8 validacijom u kliničkom okruženju, uz prototip spreman za tržišno testiranje.',
        'IOM-SIM ima dvostruku ulogu: ubrzati učenje jer ima jednaku kinematiku i haptički odaziv kao klinički robot te omogućiti da se stvarni zahvati iz ordinacije reproduciraju u učionici. Time se zatvara put od klinika do edukacije i stvaraju temelji za stalno unapređenje protokola.',
      ],
      en: [
        'IOM-SIM uses a digital twin of the clinical robot and enables standardised training and skills assessment for students and doctors of dental medicine. The system is built on a personalised 3D template, a dedicated "helmet" support structure that reduces load, sterilisable motors and sensors, and a real-time control layer supported by artificial intelligence that compensates for small patient movements.',
        'Target performance includes working accuracy within ±20 µm and a procedure time of 2–4 minutes per tooth — several times better than manual work and than existing robotic solutions that operate outside the oral cavity. The project starts at TRL 2 and aims to reach TRL 8 through validation in a clinical environment, with a prototype ready for market testing.',
        'IOM-SIM serves a dual role: accelerating learning, because it shares the same kinematics and haptic response as the clinical robot, and allowing real procedures from the surgery to be reproduced in the classroom. This closes the loop from clinic to education and lays the groundwork for continuous protocol improvement.',
      ],
    },
  },

  // --- Aktivnosti / Activities --------------------------------------------
  activities: {
    heading: { hr: 'Projekt obuhvaća sljedeće aktivnosti', en: 'The project comprises the following activities' },
    items: [
      { letter: 'a', hr: 'Industrijsko istraživanje', en: 'Industrial research' },
      { letter: 'b', hr: 'Eksperimentalni razvoj', en: 'Experimental development' },
      { letter: 'c', hr: 'Izrada studije izvedivosti', en: 'Feasibility study' },
      { letter: 'd', hr: 'Upravljanje projektom', en: 'Project management' },
      { letter: 'e', hr: 'Promidžba i vidljivost', en: 'Promotion and visibility' },
    ],
  },

  // --- Ciljevi i rezultati / Objectives and results ------------------------
  objectives: {
    heading: {
      hr: 'Ciljevi i očekivani rezultati projekta',
      en: 'Project objectives and expected results',
    },
    body: {
      hr: [
        'Projektom se razvija modularni 5-osni intra-oralni robot (IOM-CLINIC) za brušenje zubi koji ima za cilj skraćivanje medicinskog zahvata s trenutnih prosječnih 15 min po zubu na prosječnih 5 minuta po zubu uz postizanje visoke razine preciznosti. Ovaj robot nastoji riješiti problem različitog pristupa stomatologa kod pacijenata te opterećenje pacijenta koji nastaje kod ručnog brušenja zubi. Uz to, razvija se IOM-SIM koji će služiti kao edukacijski simulator u istraživačkim ustanovama. Projekt započinje u fazi TRL 2 te bi po završetku projekta 5-osni intra-oralni robot trebao biti u fazi TRL 8, a konačni cilj je primjenom robota osigurati brži, sigurniji i predvidljiv zahvat te standardizirana obuka.',
        'Očekivani rezultat projekta predstavlja značajan iskorak u odnosu na trenutno stanje tehnologije. Po prvi put uvodi se intraoralni robot koji je sterilizabilan, kompaktan i stabiliziran pomoću AI, a radi unutar usne šupljine bez potrebe za velikim vanjskim manipulatorima ili krutim fiksiranjem glave.',
        'U tehničkom smislu, projekt spaja nova znanja (prediktivna stabilizacija i digitalni blizanac u stvarnom vremenu) s naprednim postojećim vještinama (robotika s pet osi, upravljanje u stvarnom vremenu, haptička povratna informacija) u jedinstveni sustav koji ispunjava kliničke zahtjeve preciznosti, sigurnosti i brzine. Na hrvatskom tržištu ovakav sustav ne postoji; u EU ne postoji komercijalni intraoralni robot s pet stupnjeva slobode i AI stabilizacijom za pripremu krunica; a globalno postoje samo izolirani pokušaji, bez ovakve integracije klinike i edukacije i bez intraoralnog dizajna s ovom razinom preciznosti i steriliziranosti.',
      ],
      en: [
        'The project develops a modular 5-axis intraoral robot (IOM-CLINIC) for tooth preparation, aiming to shorten the procedure from the current average of 15 minutes per tooth to an average of 5 minutes per tooth while achieving a high level of precision. The robot addresses the problem of inconsistent approaches between dentists, as well as the burden placed on patients during manual tooth preparation. In addition, IOM-SIM is being developed to serve as an educational simulator in research institutions. The project starts at TRL 2 and, by its completion, the 5-axis intraoral robot should reach TRL 8. The ultimate goal is to deliver a faster, safer and more predictable procedure, together with standardised training.',
        'The expected result represents a significant step beyond the current state of the art. For the first time, it introduces an intraoral robot that is sterilisable, compact and AI-stabilised, operating inside the oral cavity without the need for large external manipulators or rigid head fixation.',
        'Technically, the project combines new knowledge (predictive stabilisation and a real-time digital twin) with advanced existing capabilities (five-axis robotics, real-time control, haptic feedback) into a single system that meets clinical requirements for precision, safety and speed. No such system exists on the Croatian market; there is no commercial intraoral robot with five degrees of freedom and AI stabilisation for crown preparation in the EU; and globally there are only isolated attempts, without this integration of clinic and education and without an intraoral design at this level of precision and sterilisability.',
      ],
    },
  },
};

// Obavezna izjava o financiranju / Mandatory funding statement
const FUNDING_STATEMENT = {
  hr: 'Financira Europska unija',
  en: 'Funded by the European Union',
};

const DISCLAIMER = {
  hr: 'Financira Europska unija. Izneseni stavovi i mišljenja su stavovi i mišljenja autora i ne odražavaju nužno službena stajališta Europske unije ili Europske komisije. Ni Europska unija ni Europska komisija ne mogu se smatrati odgovornima za njih.',
  en: 'Funded by the European Union. Views and opinions expressed are however those of the author(s) only and do not necessarily reflect those of the European Union or the European Commission. Neither the European Union nor the European Commission can be held responsible for them.',
};

// ===========================================================================
// POMOĆNE KOMPONENTE / PRESENTATIONAL HELPERS
// ===========================================================================

/** Amblem EU-a na bijeloj podlozi — obavezno za tamne pozadine. */
const EuEmblem = ({ className = '', imgClassName = 'h-14 sm:h-16 w-auto' }) => (
  <div
    className={`inline-flex items-center justify-center bg-white rounded-lg px-5 py-4 shadow-sm ${className}`}
  >
    <img
      src={EU_LOGO}
      alt={EU_LOGO_ALT}
      className={imgClassName}
      width="1024"
      height="269"
      loading="eager"
    />
  </div>
);

/** Blok hrvatskog teksta s engleskim prijevodom ispod. */
const Bilingual = ({ hr, en, className = '' }) => (
  <div className={className}>
    <div className="space-y-4">
      {hr.map((p, i) => (
        <p key={`hr-${i}`} className="text-slate-700 leading-relaxed">
          {p}
        </p>
      ))}
    </div>
    <div className="mt-6 pl-5 border-l-2 border-cyan-200 space-y-4">
      <span className="inline-block text-[11px] font-semibold tracking-widest text-cyan-700 uppercase">
        English
      </span>
      {en.map((p, i) => (
        <p key={`en-${i}`} className="text-slate-500 leading-relaxed text-[15px]">
          {p}
        </p>
      ))}
    </div>
  </div>
);

const SectionTitle = ({ hr, en, icon: Icon }) => (
  <div className="mb-8">
    <div className="flex items-start gap-3">
      {Icon && (
        <span className="mt-1 w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-700 flex items-center justify-center text-white flex-shrink-0">
          <Icon className="w-5 h-5" aria-hidden="true" />
        </span>
      )}
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{hr}</h2>
        <p className="text-slate-500 mt-1">{en}</p>
      </div>
    </div>
  </div>
);

const FactCard = ({ icon: Icon, labelHr, labelEn, children, highlight = false }) => (
  <div
    className={`rounded-xl p-6 border ${
      highlight
        ? 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200'
        : 'bg-white border-slate-200'
    }`}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon
        className={`w-4 h-4 ${highlight ? 'text-cyan-700' : 'text-slate-400'}`}
        aria-hidden="true"
      />
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 leading-tight">
          {labelHr}
        </p>
        <p className="text-[11px] text-slate-400 leading-tight">{labelEn}</p>
      </div>
    </div>
    <div
      className={`font-semibold ${
        highlight ? 'text-blue-900 text-2xl' : 'text-slate-900 text-lg'
      }`}
    >
      {children}
    </div>
  </div>
);

// ===========================================================================
// STRANICA / PAGE
// ===========================================================================

const GaloEUProject = () => {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ResearchProject',
    name: project.title.hr,
    alternateName: project.title.en,
    identifier: project.code,
    description: project.results.lead.hr,
    startDate: '2026-01-02',
    endDate: '2029-01-02',
    funder: { '@type': 'Organization', name: 'Europska unija' },
    member: [
      { '@type': 'Organization', name: project.beneficiary },
      ...project.partners.map((p) => ({ '@type': 'Organization', name: p })),
    ],
  };

  return (
    <>
      <Helmet>
        <title>EU projekt IOM | Financira Europska unija | Galo Industries</title>
        <meta
          name="description"
          content={`${project.code} — ${project.title.hr}. Projekt sufinanciran sredstvima Europske unije kroz poziv IRI S3. Ukupna vrijednost ${project.finance.total}, potpora EU ${project.finance.euSupport}.`}
        />
        <link rel="canonical" href="https://galo-industries.com/eu-projekt" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="EU projekt IOM | Galo Industries" />
        <meta
          property="og:description"
          content="Razvoj prvog autonomnog 5-osnog preciznog robotskog sustava za intraoralnu pripremu zuba — projekt financiran sredstvima Europske unije."
        />
        <meta property="og:url" content="https://galo-industries.com/eu-projekt" />
        <meta property="og:image" content="https://galo-industries.com/img/eu.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main>
        {/* ================= HERO ================= */}
        <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-bl-full" />
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tl from-blue-500/10 to-transparent rounded-tr-full" />

          <Container>
            <div className="relative max-w-4xl">
              {/* AMBLEM EU — glavno mjesto, uz izjavu o financiranju */}
              <EuEmblem className="mb-8" imgClassName="h-14 sm:h-[68px] w-auto" />

              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-300 text-xs font-semibold tracking-wider uppercase">
                  <Hash className="w-3.5 h-3.5" aria-hidden="true" />
                  {project.code}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/20 text-slate-200 text-xs font-semibold tracking-wider uppercase">
                  IRI S3
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-tight mb-5">
                {project.title.hr}
              </h1>
              <p className="text-slate-400 text-lg leading-relaxed max-w-3xl">
                {project.title.en}
              </p>

              <p className="mt-8 text-cyan-300 font-semibold text-lg">
                {FUNDING_STATEMENT.hr}
                <span className="text-slate-500 font-normal">
                  {' '}
                  · {FUNDING_STATEMENT.en}
                </span>
              </p>
            </div>
          </Container>
        </section>

        {/* ================= POZIV / PROGRAM ================= */}
        <section className="py-10 bg-blue-900 text-white">
          <Container>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-cyan-300 mb-2">
                  Poziv · Call
                </p>
                <p className="font-semibold leading-snug">
                  {project.programme.call.hr}
                </p>
                <p className="text-blue-200/70 text-sm mt-1">
                  Kod poziva: {project.programme.callCode}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-cyan-300 mb-2">
                  Program · Programme
                </p>
                <p className="font-semibold leading-snug">
                  {project.programme.operationalProgramme.hr}
                </p>
                <p className="text-blue-200/70 text-sm mt-1">
                  {project.programme.operationalProgramme.en}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-cyan-300 mb-2">
                  Fond · Fund
                </p>
                <p className="font-semibold leading-snug">
                  {project.programme.fund.hr}
                </p>
                <p className="text-blue-200/70 text-sm mt-1">
                  {project.programme.fund.en}
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* ================= KLJUČNI PODACI ================= */}
        <section className="py-16 bg-slate-50">
          <Container>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <FactCard icon={Hash} labelHr="Šifra projekta" labelEn="Project code">
                {project.code}
              </FactCard>

              <FactCard icon={Building2} labelHr="Nositelj projekta" labelEn="Beneficiary">
                {project.beneficiary}
              </FactCard>

              <FactCard
                icon={CalendarDays}
                labelHr="Razdoblje provedbe"
                labelEn="Implementation period"
              >
                {project.period}
              </FactCard>

              <FactCard icon={Euro} labelHr="Ukupna vrijednost projekta" labelEn="Total project value">
                {project.finance.total}
              </FactCard>

              <FactCard icon={Euro} labelHr="Prihvatljivi troškovi" labelEn="Eligible costs">
                {project.finance.eligible}
              </FactCard>

              <FactCard
                icon={Euro}
                labelHr="Iznos koji sufinancira EU"
                labelEn="EU co-financing"
                highlight
              >
                {project.finance.euSupport}
              </FactCard>
            </div>
          </Container>
        </section>

        {/* ================= DVA REZULTATA ================= */}
        <section className="py-16 sm:py-20 bg-white">
          <Container>
            <div className="max-w-5xl">
              <SectionTitle
                hr={project.results.heading.hr}
                en={project.results.heading.en}
                icon={Layers}
              />

              <p className="text-slate-700 leading-relaxed max-w-3xl">
                {project.results.lead.hr}
              </p>
              <p className="text-slate-500 leading-relaxed max-w-3xl mt-3 text-[15px]">
                {project.results.lead.en}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                {project.results.items.map((r) => {
                  const Icon = r.icon;
                  return (
                    <div
                      key={r.name}
                      className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-7"
                    >
                      <div className="flex items-center gap-4 mb-5">
                        <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-700 flex items-center justify-center text-white flex-shrink-0">
                          <Icon className="w-6 h-6" aria-hidden="true" />
                        </span>
                        <div>
                          <span className="text-xs font-bold text-cyan-700 uppercase tracking-wider">
                            {r.letter})
                          </span>
                          <h3 className="text-xl font-bold text-slate-900 leading-tight">
                            {r.name}
                          </h3>
                        </div>
                      </div>
                      <p className="text-slate-700 leading-relaxed">{r.hr}</p>
                      <p className="text-slate-500 leading-relaxed text-[15px] mt-3 pl-4 border-l-2 border-cyan-200">
                        {r.en}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Container>
        </section>

        {/* ================= CILJANE PERFORMANSE ================= */}
        <section className="py-16 bg-slate-900 text-white">
          <Container>
            <div className="max-w-5xl">
              <h2 className="text-2xl sm:text-3xl font-bold mb-1">
                {project.metrics.heading.hr}
              </h2>
              <p className="text-slate-400 mb-10">{project.metrics.heading.en}</p>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {project.metrics.items.map((m) => {
                  const Icon = m.icon;
                  return (
                    <div
                      key={m.value}
                      className="bg-white/5 border border-white/10 rounded-xl p-6"
                    >
                      <Icon
                        className="w-5 h-5 text-cyan-400 mb-4"
                        aria-hidden="true"
                      />
                      <p className="text-2xl sm:text-3xl font-bold mb-2">{m.value}</p>
                      <p className="text-sm text-slate-300 leading-tight">{m.hr}</p>
                      <p className="text-xs text-slate-500 leading-tight mt-0.5">
                        {m.en}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Container>
        </section>

        {/* ================= KRATKI OPIS ================= */}
        <section className="py-16 sm:py-20 bg-white">
          <Container>
            <div className="max-w-4xl">
              <SectionTitle
                hr={project.summary.heading.hr}
                en={project.summary.heading.en}
                icon={FileText}
              />
              <Bilingual hr={project.summary.body.hr} en={project.summary.body.en} />
            </div>
          </Container>
        </section>

        {/* ================= AKTIVNOSTI ================= */}
        <section className="py-16 sm:py-20 bg-slate-50">
          <Container>
            <div className="max-w-4xl">
              <SectionTitle
                hr={project.activities.heading.hr}
                en={project.activities.heading.en}
                icon={CheckCircle2}
              />
              <ol className="space-y-3">
                {project.activities.items.map((a) => (
                  <li
                    key={a.letter}
                    className="flex items-start gap-4 bg-white rounded-lg border border-slate-200 px-5 py-4"
                  >
                    <span className="w-7 h-7 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-700 text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {a.letter}
                    </span>
                    <div>
                      <p className="font-semibold text-slate-900">{a.hr}</p>
                      <p className="text-sm text-slate-500">{a.en}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </Container>
        </section>

        {/* ================= CILJEVI I REZULTATI ================= */}
        <section className="py-16 sm:py-20 bg-white">
          <Container>
            <div className="max-w-4xl">
              <SectionTitle
                hr={project.objectives.heading.hr}
                en={project.objectives.heading.en}
                icon={Target}
              />
              <Bilingual hr={project.objectives.body.hr} en={project.objectives.body.en} />
            </div>
          </Container>
        </section>

        {/* ================= PARTNERI ================= */}
        <section className="py-16 sm:py-20 bg-slate-50">
          <Container>
            <div className="max-w-4xl">
              <SectionTitle hr="Projektni partneri" en="Project partners" icon={Users} />

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-5 bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
                  <p className="text-xs uppercase tracking-wider text-cyan-100 mb-1">
                    Nositelj projekta · Beneficiary
                  </p>
                  <p className="font-semibold text-lg">{project.beneficiary}</p>
                </div>
                <ul className="divide-y divide-slate-100">
                  {project.partners.map((p) => (
                    <li key={p} className="px-6 py-5 flex items-start gap-3">
                      <Users
                        className="w-5 h-5 text-slate-300 mt-0.5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span className="text-slate-700">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Container>
        </section>

        {/* ================= FINANCIRANJE + AMBLEM ================= */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white">
          <Container>
            <div className="max-w-4xl mx-auto text-center">
              {/* AMBLEM EU — drugo obavezno mjesto */}
              <EuEmblem className="mb-8" imgClassName="h-16 sm:h-20 w-auto" />

              <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                Ukupna vrijednost projekta i iznos koji sufinancira EU
              </h2>
              <p className="text-slate-400 mb-10">
                Total project value and the amount co-financed by the EU
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-left">
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                    Ukupna vrijednost projekta
                    <span className="block normal-case tracking-normal text-slate-500">
                      Total project value
                    </span>
                  </p>
                  <p className="text-2xl font-bold">{project.finance.total}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                    Prihvatljivi troškovi
                    <span className="block normal-case tracking-normal text-slate-500">
                      Eligible costs
                    </span>
                  </p>
                  <p className="text-2xl font-bold">{project.finance.eligible}</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-600 to-blue-700 rounded-xl p-6">
                  <p className="text-xs uppercase tracking-wider text-cyan-100 mb-2">
                    Udio EU u financiranju
                    <span className="block normal-case tracking-normal text-cyan-200">
                      EU co-financing
                    </span>
                  </p>
                  <p className="text-2xl font-bold">{project.finance.euSupport}</p>
                </div>
              </div>

              <div className="mt-10 inline-flex items-center gap-3 px-5 py-3 rounded-lg bg-white/5 border border-white/10">
                <CalendarDays className="w-5 h-5 text-cyan-400" aria-hidden="true" />
                <span className="text-slate-300">
                  Razdoblje provedbe · Implementation period:{' '}
                  <strong className="text-white">{project.period}</strong>
                </span>
              </div>
            </div>
          </Container>
        </section>

        {/* ================= KONTAKT ================= */}
        <section className="py-16 sm:py-20 bg-white">
          <Container>
            <div className="max-w-4xl">
              <SectionTitle
                hr="Kontakt za više informacija"
                en="Contact for more information"
                icon={Mail}
              />
              <a
                href={`mailto:${project.contactEmail}`}
                className="inline-flex items-center gap-3 px-6 py-4 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-semibold hover:from-cyan-500 hover:to-blue-600 transition-colors duration-300"
              >
                <Mail className="w-5 h-5" aria-hidden="true" />
                {project.contactEmail}
              </a>
            </div>
          </Container>
        </section>

        {/* ================= OBAVEZNA IZJAVA ================= */}
        <section className="py-12 bg-slate-100 border-t border-slate-200">
          <Container>
            <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center sm:items-start gap-8">
              {/* AMBLEM EU — treće obavezno mjesto, uz izjavu o ograničenju odgovornosti */}
              <img
                src={EU_LOGO}
                alt={EU_LOGO_ALT}
                className="h-16 w-auto flex-shrink-0"
                width="1024"
                height="269"
              />
              <div className="text-center sm:text-left">
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  {DISCLAIMER.hr}
                </p>
                <p className="text-sm text-slate-400 leading-relaxed">{DISCLAIMER.en}</p>
              </div>
            </div>
          </Container>
        </section>
      </main>

      <Footer />

      {/* Back to top */}
      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-gradient-to-r from-cyan-600 to-blue-700 text-white shadow-lg flex items-center justify-center hover:from-cyan-500 hover:to-blue-600 transition-colors duration-300"
          aria-label="Natrag na vrh / Back to top"
        >
          <ArrowUp className="w-5 h-5" aria-hidden="true" />
        </button>
      )}
    </>
  );
};

export default GaloEUProject;